import { FormikConfig } from 'formik';
import { memo, useCallback, useMemo } from 'react';
import * as yup from 'yup';
import { FormikInput } from '../../../components/formik/FormInput';
import { FormikSelect } from '../../../components/formik/FormSelect';
import { Option } from '../../../components/inputs/Select';
import { ToastType, useToastNotifications } from '../../../hooks/useToastNotifications';
import { useValidationTranslations } from '../../../hooks/useValidationTranslations';
import { useT } from '../../../translation/src';
import { OrganisationBillingFieldsFragment, useOrganisationUpdateBillingMutation } from '../../../types/graphqlTypes';
import { useCountryName, useCountryOptions } from '../../../utils/countries';
import { graphqlErrorCode } from '../../../utils/graphqlErrors';

export type BillingInfo = NonNullable<OrganisationBillingFieldsFragment['info']>;

export interface BillingFormValues {
  companyName: string;
  street: string;
  street2: string;
  city: string;
  zip: string;
  countryCode: string;
  vat: string;
  email: string;
}

const useTranslations = () => {
  const companyName = useT('company name', { swc: true });
  const street = useT('address line 1', { swc: true });
  const street2 = useT('address line 2 (optional)', { swc: true });
  const city = useT('city', { swc: true });
  const zip = useT('postal code', { swc: true });
  const country = useT('country', { swc: true });
  const vat = useT('VAT number (optional)', { swc: true });
  const email = useT('invoice email', { swc: true });
  const invalidVat = useT('is not a valid VAT number for this country', { swc: true });
  const saveFailed = useT('could not save the billing details', { swc: true });
  const notSetUp = useT('Your organisation is not set up for ordering yet.');
  const contactSales = useT('Contact sales and we will get you started.');
  return { companyName, street, street2, city, zip, country, vat, email, invalidVat, saveFailed, notSetUp, contactSales };
};

// The billing details form, for a card or a dialog: seeded from what Odoo has,
// saved to Odoo, with Odoo's objections shown on the field they are about.
export const useBillingInfoForm = ({ organisationId, info, onSaved }: { organisationId: string; info?: BillingInfo | null; onSaved?: () => void }): { formik: FormikConfig<BillingFormValues>; saving: boolean } => {
  const translations = useTranslations();
  const validationTranslations = useValidationTranslations();
  const { showToast } = useToastNotifications();
  // Saved details change what a plan costs (nobody to invoice locks the price to 0): the simulations on screen ask again.
  const [updateBilling, { loading: saving }] = useOrganisationUpdateBillingMutation({ refetchQueries: ['organisationPlanChangePreview', 'superadminPlanChangePreview', 'partnerCustomerPlanChangePreview', 'partnerCustomerBillingDatePreview'] });

  const initialValues: BillingFormValues = useMemo(
    () => ({ companyName: info?.companyName ?? '', street: info?.street ?? '', street2: info?.street2 ?? '', city: info?.city ?? '', zip: info?.zip ?? '', countryCode: info?.countryCode ?? '', vat: info?.vat ?? '', email: info?.email ?? '' }),
    [info],
  );

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        companyName: yup.string().trim().required(validationTranslations.isRequired),
        street: yup.string().trim().required(validationTranslations.isRequired),
        city: yup.string().trim().required(validationTranslations.isRequired),
        zip: yup.string().trim().required(validationTranslations.isRequired),
        countryCode: yup.string().required(validationTranslations.isRequired),
        email: yup.string().trim().email(validationTranslations.validEmail).required(validationTranslations.isRequired),
      }),
    [validationTranslations.isRequired, validationTranslations.validEmail],
  );

  const onSubmit: FormikConfig<BillingFormValues>['onSubmit'] = useCallback(
    async (values, helpers) => {
      let failed = false;
      await updateBilling({
        variables: { organisationId, info: { ...values, street2: values.street2 || undefined, vat: values.vat || undefined } },
        onError: (error) => {
          failed = true;
          const code = graphqlErrorCode(error);
          if (code === 'INVALID_VAT') helpers.setFieldError('vat', translations.invalidVat);
          else if (code === 'INVALID_COUNTRY') helpers.setFieldError('countryCode', validationTranslations.isInvalidValue);
          // No customer in Odoo to keep them on, and none sales knows of to make: theirs to set up.
          else if (code === 'NO_ODOO_CUSTOMER') showToast({ title: translations.notSetUp, text: translations.contactSales, type: ToastType.WARNING });
          else showToast({ title: translations.saveFailed, text: error.message, type: ToastType.ERROR });
        },
      });
      if (!failed) onSaved?.();
    },
    [onSaved, organisationId, showToast, translations.contactSales, translations.invalidVat, translations.notSetUp, translations.saveFailed, updateBilling, validationTranslations.isInvalidValue],
  );

  const formik: FormikConfig<BillingFormValues> = useMemo(() => ({ initialValues, validationSchema, onSubmit, enableReinitialize: true }), [initialValues, onSubmit, validationSchema]);
  return { formik, saving };
};

const BillingInfoFields_: React.FC<{ autoFocus?: boolean }> = ({ autoFocus }) => {
  const translations = useTranslations();
  const countries = useCountryOptions();
  return (
    <div className="flex flex-col">
      <FormikInput name="companyName" label={translations.companyName} autoFocus={autoFocus} autoComplete="organization" />
      <FormikInput name="street" label={translations.street} autoComplete="address-line1" />
      <FormikInput name="street2" label={translations.street2} autoComplete="address-line2" />
      <div className="flex gap-4">
        <FormikInput name="zip" label={translations.zip} className="w-1/3" autoComplete="postal-code" />
        <FormikInput name="city" label={translations.city} className="w-2/3" autoComplete="address-level2" />
      </div>
      <FormikSelect name="countryCode" label={translations.country}>
        {countries.map((country) => (
          <Option key={country.code} value={country.code}>
            {country.name}
          </Option>
        ))}
      </FormikSelect>
      <FormikInput name="vat" label={translations.vat} />
      <FormikInput name="email" type="email" label={translations.email} autoComplete="email" />
    </div>
  );
};
export const BillingInfoFields = memo(BillingInfoFields_);

// The details as they go on the invoice.
const BillingInfoSummary_: React.FC<{ info: BillingInfo }> = ({ info }) => {
  const country = useCountryName(info.countryCode);
  return (
    <div className="flex flex-col text-sm">
      <span className="font-semibold">{info.companyName}</span>
      <span>{info.street}</span>
      {info.street2 && <span>{info.street2}</span>}
      <span>
        {info.zip} {info.city}
      </span>
      <span>{country}</span>
      {info.vat && <span>{info.vat}</span>}
      {info.email && <span className="text-gray-500">{info.email}</span>}
    </div>
  );
};
export const BillingInfoSummary = memo(BillingInfoSummary_);
